import { Fragment, FunctionComponent, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";

import { updateUser } from "~/api/users/updateUser";
import MainButton from "~/components/common/mainButton";
import { useAuth } from "~/contexts/providers/auth";
import { useLanguage } from "~/contexts/providers/language";
import englishTranslations from "~/localization/en";
import frenchTranslations from "~/localization/fr";

import styles from "./updatePassword.module.scss";

interface Props {
  handleClose: () => void;
}
const UpdatePassword: FunctionComponent<Props> = ({ handleClose }) => {
  const [oldPassword, setOldPassword] = useState<string>("");
  const [passwordRequirements, setPasswordRequirements] = useState<string[]>(
    []
  );
  const [error, setError] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(true);
  const [newPassword, setNewPassword] = useState<string>("");
  const { setUser } = useAuth();
  const { language } = useLanguage();
  const queryClient = useQueryClient();

  const loginFormTexts =
    language === "En"
      ? englishTranslations.accountPage.loginForm
      : frenchTranslations.accountPage.loginForm;

  const { isLoading, mutate } = useMutation(updateUser, {
    onSuccess: (res) => {
      queryClient.invalidateQueries("user");
      toast.success(loginFormTexts.passwordUpdated);
      handleClose();
    },
    onError: (err) => {
      toast.error(loginFormTexts.passwordFailed);
    },
  });

  const missingRequirements = [
    {
      regex: /[a-z]/,
      message: loginFormTexts.lowercase,
    },
    {
      regex: /[A-Z]/,
      message: loginFormTexts.uppercase,
    },
    {
      regex: /[0-9]/,
      message: loginFormTexts.number,
    },
    {
      regex: /[^a-zA-Z0-9]/,
      message: loginFormTexts.specialCharacter,
    },
    {
      regex: /.{8,}/,
      message: loginFormTexts.passwordLength,
    },
  ];

  const checkPasswordStrength = (value: string) => {
    const nrw = missingRequirements.filter(({ regex }) => !regex.test(value));

    if (nrw.length === 0) {
      return {
        isStrong: true,
        message: [],
      };
    }

    return {
      isStrong: false,
      message: nrw.map(({ message }) => message),
    };
  };

  const handlePasswordConfirm = (value: string) => {
    if (
      (value !== newPassword && passwordRequirements.length === 0) ||
      value === ""
    ) {
      setError(true);
      setDisabled(true);
    } else {
      setDisabled(false);
      setError(false);
    }
  };

  const handleUpdatePassword = () => {
    // console.log("update password", oldPassword, newPassword);
    mutate({ oldPassword, newPassword });
  };

  useEffect(() => {
    setPasswordRequirements(checkPasswordStrength(newPassword).message);
  }, [newPassword]);
  return (
    <div className={styles.updatePasswordContainer}>
      <fieldset>
        <Fragment>
          <label className={styles.label}>{loginFormTexts.oldPassword}</label>
          <input
            type="password"
            placeholder={loginFormTexts.oldPassword}
            size={20}
            onChange={(e) => setOldPassword(e.target.value)}
          />
          <label className={styles.label}>{loginFormTexts.password}</label>
          <input
            type="password"
            placeholder={loginFormTexts.newPassword}
            size={20}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <input
            type="password"
            placeholder={loginFormTexts.confirmNewPassword}
            size={20}
            onChange={(e) => handlePasswordConfirm(e.target.value)}
          />
          {passwordRequirements && (
            <ul>
              {passwordRequirements.map((requirement, i) => (
                <li className={styles.error} key={i}>
                  {requirement}
                </li>
              ))}
            </ul>
          )}
          {error && passwordRequirements.length === 0 && (
            <p className={styles.error}>{loginFormTexts.passwordsNotMatch}</p>
          )}
          <br />
          <MainButton
            onClick={handleUpdatePassword}
            isLoading={isLoading}
            disabled={disabled}
          >
            {loginFormTexts.passwordUpdateFormButton}
          </MainButton>
        </Fragment>
      </fieldset>
    </div>
  );
};

export default UpdatePassword;
